import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import {
  getSuperAdminSchool,
  setSuperAdminSchoolAccessPreset,
  updateSuperAdminSchoolModule
} from 'Src/helpers/service/superadmin/schools';

const MODULES = [
  { key: 'SPORT', label: 'Sport', icon: '🏃' },
  { key: 'CLUBS', label: 'Clubs', icon: '🎪' },
  { key: 'AWARDS', label: 'Awards', icon: '🏅' },
  { key: 'COMMUNICATIONS', label: 'Communications', icon: '💬' },
  { key: 'CHALLENGES', label: 'Challenges', icon: '🎯' },
  { key: 'LEAGUES', label: 'Leagues', icon: '🏆' },
  { key: 'TOURNAMENTS', label: 'Tournaments', icon: '🎖️' },
  { key: 'MIS', label: 'MIS Integration', icon: '🔗' }
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active', badge: 'badge-success' },
  { value: 'TRIAL', label: 'Trial', badge: 'badge-warning' },
  { value: 'INACTIVE', label: 'Inactive', badge: 'badge-secondary' }
];

const SOURCE_OPTIONS = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'BUNDLE', label: 'Bundle' },
  { value: 'LEGACY', label: 'Legacy' }
];

const ALL_PRESETS: Array<{ key: string; label: string }> = [
  { key: 'ADMIN', label: 'Admin' },
  { key: 'MANAGER', label: 'Manager' },
  { key: 'TEACHER', label: 'Teacher' },
  { key: 'COACH', label: 'Coach' },
  { key: 'PARENT', label: 'Parent' },
  { key: 'STUDENT', label: 'Student' },
  { key: 'GOVERNOR', label: 'Governor' },
  { key: 'SLT', label: 'School leadership (SLT)' },
  { key: 'PUBLIC_BLOGGER', label: 'Public Blogger' },
  { key: 'TOURNAMENT_FOLLOWER', label: 'Tournament Follower' }
];

const PRESET_SHORTCUTS = [
  { label: 'Admin only', presets: ['ADMIN'] },
  { label: 'Admin + Parent', presets: ['ADMIN', 'PARENT'] },
  { label: 'Full staff access', presets: ['ADMIN', 'MANAGER', 'TEACHER', 'COACH', 'PARENT', 'STUDENT', 'GOVERNOR', 'SLT'] }
];

interface ModuleSubscription {
  module: string;
  status: 'ACTIVE' | 'TRIAL' | 'INACTIVE';
  description?: string;
  validFrom?: string;
  validUntil?: string;
  source?: string;
  isTrialAvailable?: boolean;
  trialEverActivated?: boolean;
  trialFirstStartedAt?: string;
  trialLastEndedAt?: string;
  limits?: {
    maxStudents?: number;
    maxStaff?: number;
    maxSchools?: number;
  };
  note?: string;
  updatedAt?: string;
}

interface EditDraft {
  status: 'ACTIVE' | 'TRIAL' | 'INACTIVE';
  description: string;
  validFrom: string;
  validUntil: string;
  source: string;
  includedWithModule: string;
  isTrialAvailable: boolean;
  limitStudents: string;
  limitStaff: string;
  limitSchools: string;
  note: string;
}

interface Props {
  schoolId: string;
}

interface State {
  isLoading: boolean;
  isSaving: string | null;
  errorMessage: string;
  subscriptions: ModuleSubscription[];
  editingModule: string | null;
  draft: EditDraft | null;
  showAccessPresetModal: boolean;
  accessPresetDraft: {
    selectedPresets: string[];
    canAcceptStaffRoles: boolean;
    canPublishWebSite: boolean;
  };
  isSavingAccessPreset: boolean;
  accessPresetError: string;
  currentAllowedPresets: Record<string, boolean>;
}

function toDateInput(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function fromDateInput(val: string): string | undefined {
  if (!val) return undefined;
  return new Date(val).toISOString();
}

function getBadgeClass(status: string): string {
  return STATUS_OPTIONS.find(o => o.value === status)?.badge ?? 'badge-secondary';
}

function getStatusLabel(status: string): string {
  return STATUS_OPTIONS.find(o => o.value === status)?.label ?? status;
}

function buildDraft(sub: ModuleSubscription | undefined): EditDraft {
  return {
    status: sub?.status ?? 'INACTIVE',
    description: sub?.description ?? '',
    validFrom: toDateInput(sub?.validFrom),
    validUntil: toDateInput(sub?.validUntil),
    source: sub?.source ?? 'MANUAL',
    includedWithModule: (sub as any)?.includedWithModule ?? '',
    isTrialAvailable: sub?.isTrialAvailable ?? true,
    limitStudents: sub?.limits?.maxStudents != null ? String(sub.limits.maxStudents) : '',
    limitStaff: sub?.limits?.maxStaff != null ? String(sub.limits.maxStaff) : '',
    limitSchools: sub?.limits?.maxSchools != null ? String(sub.limits.maxSchools) : '',
    note: sub?.note ?? ''
  };
}

function buildPayload(draft: EditDraft) {
  const limits: any = {};
  if (draft.limitStudents !== '') limits.maxStudents = Number(draft.limitStudents);
  if (draft.limitStaff !== '') limits.maxStaff = Number(draft.limitStaff);
  if (draft.limitSchools !== '') limits.maxSchools = Number(draft.limitSchools);

  return {
    status: draft.status,
    description: draft.description || undefined,
    validFrom: fromDateInput(draft.validFrom),
    validUntil: fromDateInput(draft.validUntil),
    source: draft.source,
    includedWithModule: draft.includedWithModule || null,
    isTrialAvailable: draft.isTrialAvailable,
    limits: Object.keys(limits).length > 0 ? limits : {},
    note: draft.note || undefined
  };
}

export class SuperAdminSchoolModules extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: null,
      errorMessage: '',
      subscriptions: [],
      editingModule: null,
      draft: null,
      showAccessPresetModal: false,
      accessPresetDraft: {
        selectedPresets: ['ADMIN'],
        canAcceptStaffRoles: false,
        canPublishWebSite: false
      },
      isSavingAccessPreset: false,
      accessPresetError: '',
      currentAllowedPresets: {}
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ isLoading: true, errorMessage: '' });

    getSuperAdminSchool(this.props.schoolId)
      .then(school => {
        const currentAllowedPresets = school.allowedPermissionPresets || {};
        // Derive currently-selected presets for the modal default
        const selectedPresets = ALL_PRESETS.map(p => p.key).filter(k => currentAllowedPresets[k] !== false);

        this.setState({
          isLoading: false,
          subscriptions: school.moduleSubscriptions || [],
          currentAllowedPresets,
          accessPresetDraft: {
            selectedPresets: selectedPresets.length > 0 ? selectedPresets : ['ADMIN'],
            canAcceptStaffRoles: school.canAcceptStaffRoles || false,
            canPublishWebSite: school.canPublishWebSite || false
          }
        });
      })
      .catch(err => {
        console.error('Cannot load school modules', err);
        this.setState({ isLoading: false, errorMessage: 'Could not load module subscriptions.' });
      });
  };

  getSubscription(moduleKey: string): ModuleSubscription | undefined {
    return this.state.subscriptions.find(s => s.module === moduleKey);
  }

  onEditClick = (moduleKey: string) => {
    const sub = this.getSubscription(moduleKey);
    this.setState({ editingModule: moduleKey, draft: buildDraft(sub) });
  };

  onCancelClick = () => {
    this.setState({ editingModule: null, draft: null });
  };

  onDraftChange = (field: keyof EditDraft, value: any) => {
    this.setState(prev => ({
      draft: prev.draft ? { ...prev.draft, [field]: value } : null
    }));
  };

  onSaveClick = async (moduleKey: string) => {
    const { draft } = this.state;
    if (!draft) return;

    this.setState({ isSaving: moduleKey, errorMessage: '' });

    try {
      const school = await updateSuperAdminSchoolModule(this.props.schoolId, moduleKey, buildPayload(draft));
      this.setState({
        isSaving: null,
        editingModule: null,
        draft: null,
        subscriptions: school.moduleSubscriptions || []
      });
    } catch (err) {
      console.error('Cannot save module subscription', err);
      this.setState({ isSaving: null, errorMessage: `Could not save ${moduleKey}.` });
    }
  };

  // ─── Access Preset Modal ────────────────────────────────────────────────────

  onOpenAccessPresetModal = () => {
    this.setState({ showAccessPresetModal: true, accessPresetError: '' });
  };

  onCloseAccessPresetModal = () => {
    this.setState({ showAccessPresetModal: false, accessPresetError: '' });
  };

  onAccessPresetToggle = (key: string) => {
    this.setState(prev => {
      const current = prev.accessPresetDraft.selectedPresets;
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return {
        accessPresetDraft: { ...prev.accessPresetDraft, selectedPresets: next }
      };
    });
  };

  onAccessPresetShortcut = (presets: string[]) => {
    this.setState(prev => ({
      accessPresetDraft: { ...prev.accessPresetDraft, selectedPresets: presets }
    }));
  };

  onAccessPresetFlagChange = (flag: 'canAcceptStaffRoles' | 'canPublishWebSite', value: boolean) => {
    this.setState(prev => ({
      accessPresetDraft: { ...prev.accessPresetDraft, [flag]: value }
    }));
  };

  onSaveAccessPreset = async () => {
    const { accessPresetDraft } = this.state;
    if (accessPresetDraft.selectedPresets.length === 0) {
      this.setState({ accessPresetError: 'Select at least one preset.' });
      return;
    }

    this.setState({ isSavingAccessPreset: true, accessPresetError: '' });

    try {
      const school = await setSuperAdminSchoolAccessPreset(
        this.props.schoolId,
        accessPresetDraft.selectedPresets,
        accessPresetDraft.canAcceptStaffRoles,
        accessPresetDraft.canPublishWebSite
      );

      const currentAllowedPresets = school.allowedPermissionPresets || {};
      this.setState({
        isSavingAccessPreset: false,
        showAccessPresetModal: false,
        currentAllowedPresets
      });
    } catch (err) {
      console.error('Cannot save access preset', err);
      this.setState({ isSavingAccessPreset: false, accessPresetError: 'Could not save. Please try again.' });
    }
  };

  renderAccessPresetModal() {
    const { showAccessPresetModal, accessPresetDraft, isSavingAccessPreset, accessPresetError } = this.state;
    if (!showAccessPresetModal) return null;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={this.onCloseAccessPresetModal}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 6,
            padding: 24,
            width: 480,
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <h5 className="mb-1">Set access preset</h5>
          <p className="text-muted small mb-3">
            Restrict which roles users can request for this school. Deselected roles will be blocked from registration.
          </p>

          {/* Quick shortcuts */}
          <div className="mb-3">
            <div className="small font-weight-bold mb-1">Quick select:</div>
            <div className="d-flex flex-wrap" style={{ gap: 6 }}>
              {PRESET_SHORTCUTS.map(s => (
                <button
                  key={s.label}
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => this.onAccessPresetShortcut(s.presets)}
                  disabled={isSavingAccessPreset}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preset checkboxes */}
          <div className="mb-3">
            <div className="small font-weight-bold mb-1">Allowed roles:</div>
            <div className="d-flex flex-wrap" style={{ gap: '4px 16px' }}>
              {ALL_PRESETS.map(p => (
                <label key={p.key} className="mb-1" style={{ cursor: 'pointer', minWidth: 160 }}>
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={accessPresetDraft.selectedPresets.includes(p.key)}
                    onChange={() => this.onAccessPresetToggle(p.key)}
                    disabled={isSavingAccessPreset}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </div>

          {/* Flags */}
          <div className="mb-3 border-top pt-3">
            <div className="small font-weight-bold mb-2">School capabilities:</div>
            <label className="d-block mb-1" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="mr-2"
                checked={accessPresetDraft.canAcceptStaffRoles}
                onChange={e => this.onAccessPresetFlagChange('canAcceptStaffRoles', e.target.checked)}
                disabled={isSavingAccessPreset}
              />
              <span>Can accept staff roles</span>
            </label>
            <label className="d-block" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="mr-2"
                checked={accessPresetDraft.canPublishWebSite}
                onChange={e => this.onAccessPresetFlagChange('canPublishWebSite', e.target.checked)}
                disabled={isSavingAccessPreset}
              />
              <span>Can publish public website</span>
            </label>
          </div>

          {accessPresetError && <div className="alert alert-danger py-1 small mb-3">{accessPresetError}</div>}

          <div className="d-flex justify-content-end" style={{ gap: 8 }}>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={this.onCloseAccessPresetModal}
              disabled={isSavingAccessPreset}
            >
              Cancel
            </button>
            <button
              className="btn btn-warning btn-sm"
              onClick={this.onSaveAccessPreset}
              disabled={isSavingAccessPreset}
            >
              {isSavingAccessPreset ? 'Saving…' : 'Apply preset'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderReadRow(mod: { key: string; label: string; icon: string }) {
    const sub = this.getSubscription(mod.key);
    const status = sub?.status ?? 'INACTIVE';

    return (
      <tr key={mod.key}>
        <td style={{ width: 40, textAlign: 'center', fontSize: 20 }}>{mod.icon}</td>
        <td>
          <div className="font-weight-bold">{mod.label}</div>
          <div className="text-muted small">{mod.key}</div>
          {sub?.description && <div className="text-muted small font-italic mt-1">{sub.description}</div>}
        </td>
        <td>
          <span className={`badge ${getBadgeClass(status)}`}>{getStatusLabel(status)}</span>
        </td>
        <td className="text-muted small">
          {sub?.validFrom ? toDateInput(sub.validFrom) : '—'} → {sub?.validUntil ? toDateInput(sub.validUntil) : '—'}
        </td>
        <td className="text-muted small">{sub?.source ?? '—'}</td>
        <td className="text-muted small">
          {(sub as any)?.includedWithModule ? (
            <span className="badge badge-info">{(sub as any).includedWithModule}</span>
          ) : (
            '—'
          )}
        </td>
        <td className="text-muted small">
          {sub?.isTrialAvailable !== false ? '✓ Trial available' : '✗ No trial'}
          {sub?.trialEverActivated ? <span className="ml-2 text-info">activated</span> : null}
        </td>
        <td className="text-muted small">
          {sub?.limits?.maxStudents != null ? `${sub.limits.maxStudents} students` : ''}
          {sub?.limits?.maxStaff != null ? ` / ${sub.limits.maxStaff} staff` : ''}
        </td>
        <td>
          <button className="btn btn-sm btn-outline-primary" onClick={() => this.onEditClick(mod.key)}>
            Edit
          </button>
        </td>
      </tr>
    );
  }

  renderEditRow(mod: { key: string; label: string; icon: string }) {
    const { draft, isSaving } = this.state;
    if (!draft) return null;
    const saving = isSaving === mod.key;

    return (
      <tr key={mod.key} className="table-active">
        <td colSpan={8} className="p-3">
          <div className="d-flex align-items-center mb-3">
            <span style={{ fontSize: 22, marginRight: 10 }}>{mod.icon}</span>
            <strong style={{ fontSize: 16 }}>{mod.label}</strong>
            <span className="text-muted small ml-2">({mod.key})</span>
          </div>

          <div className="row">
            {/* Status */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Status</label>
              <select
                className="form-control form-control-sm"
                value={draft.status}
                onChange={e => this.onDraftChange('status', e.target.value)}
                disabled={saving}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Source */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Source</label>
              <select
                className="form-control form-control-sm"
                value={draft.source}
                onChange={e => this.onDraftChange('source', e.target.value)}
                disabled={saving}
              >
                {SOURCE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Included with module */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Included with</label>
              <select
                className="form-control form-control-sm"
                value={draft.includedWithModule}
                onChange={e => this.onDraftChange('includedWithModule', e.target.value)}
                disabled={saving}
              >
                <option value="">— none —</option>
                {MODULES.map(m => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Valid From */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Valid from</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draft.validFrom}
                onChange={e => this.onDraftChange('validFrom', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Valid Until */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Valid until</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={draft.validUntil}
                onChange={e => this.onDraftChange('validUntil', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Trial available */}
            <div className="col-md-2 d-flex align-items-end pb-1">
              <label className="mb-0" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={draft.isTrialAvailable}
                  onChange={e => this.onDraftChange('isTrialAvailable', e.target.checked)}
                  disabled={saving}
                />
                <span className="small font-weight-bold">Trial available</span>
              </label>
            </div>
          </div>

          <div className="row mt-2">
            {/* Limits */}
            <div className="col-md-2">
              <label className="small font-weight-bold">Max students</label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={draft.limitStudents}
                placeholder="unlimited"
                onChange={e => this.onDraftChange('limitStudents', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="col-md-2">
              <label className="small font-weight-bold">Max staff</label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={draft.limitStaff}
                placeholder="unlimited"
                onChange={e => this.onDraftChange('limitStaff', e.target.value)}
                disabled={saving}
              />
            </div>
            <div className="col-md-2">
              <label className="small font-weight-bold">Max schools</label>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={draft.limitSchools}
                placeholder="unlimited"
                onChange={e => this.onDraftChange('limitSchools', e.target.value)}
                disabled={saving}
              />
            </div>

            {/* Note */}
            <div className="col-md-4">
              <label className="small font-weight-bold">Note</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={draft.note}
                placeholder="Optional internal note"
                onChange={e => this.onDraftChange('note', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          <div className="col-md-6">
            <label className="small font-weight-bold">Description</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={draft.description}
              placeholder="Public-facing description for this module"
              onChange={e => this.onDraftChange('description', e.target.value)}
              disabled={saving}
            />
          </div>

          <div className="mt-3">
            <button className="btn btn-sm btn-primary mr-2" onClick={() => this.onSaveClick(mod.key)} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-sm btn-outline-secondary" onClick={this.onCancelClick} disabled={saving}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  render() {
    const { isLoading, errorMessage, editingModule, currentAllowedPresets } = this.state;

    if (isLoading) return <Loader />;

    const activePresetLabels = ALL_PRESETS.filter(p => currentAllowedPresets[p.key] !== false)
      .map(p => p.label)
      .join(', ');

    return (
      <div>
        {this.renderAccessPresetModal()}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Module subscriptions</h4>
            <div className="text-muted">Manage which platform modules are active for this school.</div>
          </div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <div className="text-right mr-2">
              <div className="small text-muted">Allowed roles</div>
              <div
                className="small font-weight-bold"
                style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {activePresetLabels || '—'}
              </div>
            </div>
            <button className="btn btn-warning btn-sm" onClick={this.onOpenAccessPresetModal}>
              Set access preset
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={this.load}>
              Refresh
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <table className="table table-bordered table-sm mb-0">
          <thead className="thead-light">
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Module</th>
              <th>Status</th>
              <th>Validity</th>
              <th>Source</th>
              <th>Included with</th>
              <th>Trial</th>
              <th>Limits</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map(mod => (editingModule === mod.key ? this.renderEditRow(mod) : this.renderReadRow(mod)))}
          </tbody>
        </table>
      </div>
    );
  }
}
